
import { useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
    Accordion,
    AccordionContent,
    AccordionItem,
    AccordionTrigger,
} from '@/components/ui/accordion';
import { AlertCircle, CheckCircle2, Clock, Copy, Hash, Layers, Loader2, X, Eye, XCircle, ChevronRight } from 'lucide-react';
import type { CheckpointData, LogEntry, AggregatedCheckpointData } from '@/types/workflow-replay';
import { getStatusVariant, formatDuration } from '@/types/workflow-replay';
import { JsonViewer, LogViewer } from './replay';
import { cn } from '@/lib/utils';

interface NodeInspectorDrawerProps {
    isOpen: boolean;
    onClose: () => void;
    stateName: string;
    /** Optional capability name to display under the step name */
    capabilityName?: string;
    checkpoint: CheckpointData | null;
    logs: LogEntry[];
    /** Aggregated checkpoint data for nodes inside map iterations */
    aggregated: AggregatedCheckpointData | null;
    isLoading?: boolean;
}

/**
 * Drawer component for inspecting workflow node data in replay mode.
 *
 * Shows tabs for:
 * - Input: The input data passed to the node
 * - Context: Logs and context before/after execution
 * - Output: The result or error from the node
 * - Iterations: (For map nodes) Individual iteration checkpoints
 */
export function NodeInspectorDrawer({
    isOpen,
    onClose,
    stateName,
    capabilityName,
    checkpoint,
    logs,
    aggregated,
    isLoading,
}: NodeInspectorDrawerProps) {
    const [activeTab, setActiveTab] = useState<'input' | 'context' | 'output' | 'logs' | 'iterations'>('input');
    const [selectedIteration, setSelectedIteration] = useState<number | null>(null);
    
    // State for full text drawer
    const [fullTextData, setFullTextData] = useState<{ text: string; keyName?: string } | null>(null);
    
    const handleTruncatedStringClick = (fullText: string, keyName?: string) => {
        setFullTextData({ text: fullText, keyName });
    };
    
    const closeFullTextDrawer = () => {
        setFullTextData(null);
    };

    // If viewing a specific iteration, use that checkpoint; otherwise use the latest/only one
    const displayCheckpoint = selectedIteration !== null && aggregated
        ? aggregated.iterations.get(selectedIteration) || checkpoint
        : checkpoint;

    // Determine if this is a map iteration node
    const isMapIterationNode = !!aggregated && aggregated.totalIterations > 0;

    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
                                {/* Header */}
                                <div className="px-10 py-4 border-b border-slate-100 bg-slate-50/50 flex-shrink-0">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h2 className="flex items-center gap-3 text-xl font-semibold text-slate-900">
                                                <Eye size={28} className="text-indigo-600" />
                                                <div className="flex flex-col">
                                                    <span className="font-mono">{stateName}</span>
                                                    {capabilityName && (
                                                        <span className="text-sm font-normal text-slate-500">{capabilityName}</span>
                                                    )}
                                                </div>
                                                {isMapIterationNode ? (
                                                    <MapIterationStatusBadge aggregated={aggregated} />
                                                ) : checkpoint && (
                                                    <Badge variant={getStatusVariant(checkpoint.status)}>
                                                        {checkpoint.status}
                                                    </Badge>
                                                )}
                                                {isLoading && (
                                                    <Loader2 className="h-4 w-4 animate-spin text-slate-400" />
                                                )}
                                            </h2>
                                                <div className="mt-3 text-sm text-slate-500">
                                                    {isMapIterationNode ? (
                                                        <MapIterationSummary 
                                                            aggregated={aggregated} 
                                                            selectedIteration={selectedIteration}
                                                        />
                                                    ) : checkpoint ? (
                                                        <div className="flex items-center gap-3">
                                                            <span className="flex items-center gap-1">
                                                                <Hash className="h-3 w-3" />
                                                                Attempt {checkpoint.attempt}
                                                            </span>
                                                            {checkpoint.duration_ms !== null && (
                                                                <span className="flex items-center gap-1">
                                                                    <Clock className="h-3 w-3" />
                                                                    {formatDuration(checkpoint.duration_ms)}
                                                                </span>
                                                            )}
                                                            {checkpoint.span_id && (
                                                                <span className="font-mono text-xs text-slate-400">
                                                                    span: {checkpoint.span_id.slice(0, 8)}...
                                                                </span>
                                                            )}
                                                        </div>
                                                    ) : (
                                                        <span className="text-slate-400">No checkpoint data available</span>
                                                    )}
                                                </div>
                                        </div>
                                        <button
                                            type="button"
                                            onClick={onClose}
                                            className="rounded-full p-2 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-slate-900"
                                        >
                                            <X size={20} />
                                        </button>
                                    </div>
                                </div>

                                {/* Content */}
                                <div className="relative flex-1 min-h-0 overflow-hidden flex flex-col">
                                <div className="flex-1 overflow-y-auto my-4 px-10">
                                    <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as typeof activeTab)}>
                                        <TabsList className={cn("grid w-full", isMapIterationNode ? "grid-cols-5" : "grid-cols-4")}>
                                            <TabsTrigger value="input">Input</TabsTrigger>
                                            <TabsTrigger value="context">Context</TabsTrigger>
                                            <TabsTrigger value="output" className="flex items-center gap-1">
                                                Output
                                                {displayCheckpoint?.error && (
                                                    <AlertCircle className="ml-1 h-3 w-3 text-red-500" />
                                                )}
                                            </TabsTrigger>
                                            <TabsTrigger value="logs" className="flex items-center gap-1">
                                                Logs
                                                {logs.length > 0 && (
                                                    <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                                                        {logs.length}
                                                    </Badge>
                                                )}
                                            </TabsTrigger>
                                            {isMapIterationNode && (
                                                <TabsTrigger value="iterations" className="flex items-center gap-1">
                                                    <Layers className="h-3 w-3" />
                                                    Iterations
                                                    <Badge variant="secondary" className="ml-1 h-4 px-1 text-[10px]">
                                                        {aggregated.totalIterations}
                                                    </Badge>
                                                </TabsTrigger>
                                            )}
                                        </TabsList>

                                        <div className="mt-4 mx-6">
                                            <TabsContent value="input" className="mt-0">
                                                <InputTab checkpoint={displayCheckpoint} onTruncatedStringClick={handleTruncatedStringClick} />
                                            </TabsContent>

                                            <TabsContent value="context" className="mt-0">
                                                <ContextTab checkpoint={displayCheckpoint} onTruncatedStringClick={handleTruncatedStringClick} />
                                            </TabsContent>

                                            <TabsContent value="output" className="mt-0">
                                                <OutputTab checkpoint={displayCheckpoint} onTruncatedStringClick={handleTruncatedStringClick} />
                                            </TabsContent>

                                            <TabsContent value="logs" className="mt-0">
                                                <LogsTab checkpoint={displayCheckpoint} logs={logs} />
                                            </TabsContent>

                                            {isMapIterationNode && (
                                                <TabsContent value="iterations" className="mt-0">
                                                    <IterationsTab 
                                                        aggregated={aggregated}
                                                        selectedIteration={selectedIteration}
                                                        onSelectIteration={setSelectedIteration}
                                                    />
                                                </TabsContent>
                                            )}
                                        </div>
                                    </Tabs>
                                </div>
                                
                                {/* Full Text Bottom Drawer */}
                                <FullTextDrawer 
                                    isOpen={fullTextData !== null}
                                    onClose={closeFullTextDrawer}
                                    text={fullTextData?.text ?? ''}
                                    keyName={fullTextData?.keyName}
                                />
                                </div>
        </SlideDrawer>
    );
}

/**
 * Status badge for map iteration nodes showing aggregated status.
 */
function MapIterationStatusBadge({ aggregated }: { aggregated: AggregatedCheckpointData }) {
    const { aggregatedStatus, completedCount, failedCount, totalIterations } = aggregated;
    
    switch (aggregatedStatus) {
        case 'completed':
            return (
                <Badge variant="default" className="bg-green-600">
                    <CheckCircle2 className="mr-1 h-3 w-3" />
                    {completedCount}/{totalIterations} completed
                </Badge>
            );
        case 'failed':
            return (
                <Badge variant="destructive">
                    <XCircle className="mr-1 h-3 w-3" />
                    {failedCount} failed
                </Badge>
            );
        case 'running':
            return (
                <Badge variant="secondary">
                    <Loader2 className="mr-1 h-3 w-3 animate-spin" />
                    running
                </Badge>
            );
        case 'partial':
            return (
                <Badge variant="outline" className="border-amber-300 text-amber-700">
                    {completedCount}/{totalIterations} completed
                </Badge>
            );
        default:
            return (
                <Badge variant="outline">
                    {totalIterations} iterations
                </Badge>
            );
    }
}

/**
 * Summary line for map iteration nodes.
 */
function MapIterationSummary({ 
    aggregated, 
    selectedIteration 
}: { 
    aggregated: AggregatedCheckpointData;
    selectedIteration: number | null;
}) {
    return (
        <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
                <Layers className="h-3 w-3" />
                Map: {aggregated.mapNodeId}
            </span>
            <span className="flex items-center gap-1">
                <Hash className="h-3 w-3" />
                {aggregated.totalIterations} iterations
            </span>
            {aggregated.avgDurationMs > 0 && (
                <span className="flex items-center gap-1">
                    <Clock className="h-3 w-3" />
                    avg {formatDuration(aggregated.avgDurationMs)}
                </span>
            )}
            {selectedIteration !== null && (
                <span className="text-indigo-600 font-medium">
                    Viewing iteration #{selectedIteration}
                </span>
            )}
        </div>
    );
}

interface TabProps {
    checkpoint: CheckpointData | null;
    onTruncatedStringClick?: (fullText: string, keyName?: string) => void;
}

/**
 * Input tab showing the input data passed to the node.
 */
function InputTab({ checkpoint, onTruncatedStringClick }: TabProps) {
    if (!checkpoint) {
        return <EmptyState message="No input data available" />;
    }

    return (
        <div className="space-y-4">
            <div>
                <h4 className="mb-2 text-sm font-medium text-slate-700">Input Data</h4>
                <JsonViewer data={checkpoint.input_data} onTruncatedStringClick={onTruncatedStringClick} />
            </div>
        </div>
    );
}

/**
 * Context tab showing context before/after execution.
 * 
 * - "Before" shows the full state snapshot before the step ran
 * - "After" shows only the variables that were changed by this step
 *   (uses context_changes when available, falls back to context_after for legacy data)
 */
function ContextTab({ checkpoint, onTruncatedStringClick }: TabProps) {
    // Determine what to show for "after" - prefer granular changes over full snapshot
    const hasContextChanges = checkpoint?.context_changes !== null && checkpoint?.context_changes !== undefined;
    const afterData = hasContextChanges ? checkpoint.context_changes : checkpoint?.context_after;
    const afterLabel = hasContextChanges ? 'State Changes (Written by Step)' : 'Context After Execution';
    const afterEmptyMessage = hasContextChanges ? 'No state changes made by this step' : 'No context data';
    const beforeData = checkpoint?.context_before;
    const hasBeforeData = !!beforeData && Object.keys(beforeData).length > 0;

    return (
        <div className="space-y-4">
            {/* Context accordion - both sections expanded by default */}
            <Accordion type="multiple" defaultValue={['context-before', 'context-after']}>
                <AccordionItem value="context-before">
                    <AccordionTrigger className="text-sm">
                        Context Before Execution
                        <span className="ml-2 text-xs text-slate-400 font-normal">(full state)</span>
                    </AccordionTrigger>
                    <AccordionContent>
                        {hasBeforeData ? (
                            <JsonViewer data={beforeData} maxInitialDepth={1} onTruncatedStringClick={onTruncatedStringClick} />
                        ) : (
                            <EmptyState message="No context data" />
                        )}
                    </AccordionContent>
                </AccordionItem>

                <AccordionItem value="context-after">
                    <AccordionTrigger className="text-sm">
                        {afterLabel}
                        {hasContextChanges && (
                            <span className="ml-2 text-xs text-slate-400 font-normal">(delta only)</span>
                        )}
                    </AccordionTrigger>
                    <AccordionContent>
                        {afterData && Object.keys(afterData).length > 0 ? (
                            <JsonViewer data={afterData} maxInitialDepth={1} onTruncatedStringClick={onTruncatedStringClick} />
                        ) : (
                            <EmptyState message={afterEmptyMessage} />
                        )}
                    </AccordionContent>
                </AccordionItem>
            </Accordion>
        </div>
    );
}

/**
 * Logs tab showing execution logs.
 */
function LogsTab({ checkpoint, logs }: TabProps & { logs: LogEntry[] }) {
    return (
        <div className="space-y-4">
            <div>
                <h4 className="mb-2 text-sm font-medium text-slate-700">Execution Logs</h4>
                {logs.length > 0 ? (
                    <LogViewer logs={logs} maxHeight="400px" />
                ) : (
                    <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-500">
                        {checkpoint?.span_id 
                            ? 'No logs found for this span'
                            : 'No span_id available for log correlation'}
                    </div>
                )}
            </div>
        </div>
    );
}

/**
 * Output tab showing the result or error from the node.
 */
function OutputTab({ checkpoint, onTruncatedStringClick }: TabProps) {
    if (!checkpoint) {
        return <EmptyState message="No output data available" />;
    }

    // Show error if present
    if (checkpoint.error) {
        return (
            <div className="space-y-4">
                <div className="rounded-md border border-red-200 bg-red-50 p-4">
                    <div className="flex items-start gap-2">
                        <AlertCircle className="mt-0.5 h-4 w-4 flex-shrink-0 text-red-500" />
                        <div className="flex-1">
                            <h4 className="text-sm font-medium text-red-800">
                                {checkpoint.error.type || 'Error'}
                            </h4>
                            <p className="mt-1 text-sm text-red-700">
                                {checkpoint.error.message || 'An error occurred'}
                            </p>
                            {checkpoint.error.code && (
                                <p className="mt-1 text-xs text-red-600">
                                    Code: {checkpoint.error.code}
                                </p>
                            )}
                        </div>
                    </div>
                </div>

                {checkpoint.error.trace && checkpoint.error.trace.length > 0 && (
                    <Accordion type="single" collapsible>
                        <AccordionItem value="trace">
                            <AccordionTrigger className="text-sm text-slate-600">
                                Stack Trace ({checkpoint.error.trace.length} frames)
                            </AccordionTrigger>
                            <AccordionContent>
                                <div className="max-h-[300px] overflow-auto rounded-md bg-slate-900 p-3">
                                    <pre className="font-mono text-xs text-slate-300">
                                        {checkpoint.error.trace.map((frame, i) => {
                                            const f = frame as Record<string, unknown>;
                                            return (
                                                <div key={i} className="py-0.5">
                                                    <span className="text-slate-500">#{i}</span>{' '}
                                                    <span className="text-cyan-400">{String(f.class || '')}</span>
                                                    <span className="text-slate-400">::</span>
                                                    <span className="text-yellow-400">{String(f.function || '')}</span>
                                                    {f.file ? (
                                                        <span className="text-slate-500">
                                                            {' '}at {String(f.file)}:{String(f.line || '')}
                                                        </span>
                                                    ) : null}
                                                </div>
                                            );
                                        })}
                                    </pre>
                                </div>
                            </AccordionContent>
                        </AccordionItem>
                    </Accordion>
                )}
            </div>
        );
    }

    // Show result
    return (
        <div className="space-y-4">
            <div>
                <h4 className="mb-2 text-sm font-medium text-slate-700">Result</h4>
                {checkpoint.result ? (
                    <JsonViewer data={checkpoint.result} onTruncatedStringClick={onTruncatedStringClick} />
                ) : (
                    <EmptyState message="No result data" />
                )}
            </div>
        </div>
    );
}

/**
 * Iterations tab for map nodes - shows all iterations with status.
 */
function IterationsTab({ 
    aggregated, 
    selectedIteration,
    onSelectIteration,
}: { 
    aggregated: AggregatedCheckpointData;
    selectedIteration: number | null;
    onSelectIteration: (index: number | null) => void;
}) {
    // Sort iterations by index
    const sortedIterations = Array.from(aggregated.iterations.entries())
        .sort(([a], [b]) => a - b);

    return (
        <div className="space-y-4">
            {/* Summary stats */}
            <div className="grid grid-cols-4 gap-3">
                <div className="rounded-lg border border-green-200 bg-green-50 p-3 text-center">
                    <div className="text-2xl font-bold text-green-700">{aggregated.completedCount}</div>
                    <div className="text-xs text-green-600">Completed</div>
                </div>
                <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-center">
                    <div className="text-2xl font-bold text-red-700">{aggregated.failedCount}</div>
                    <div className="text-xs text-red-600">Failed</div>
                </div>
                <div className="rounded-lg border border-blue-200 bg-blue-50 p-3 text-center">
                    <div className="text-2xl font-bold text-blue-700">{aggregated.runningCount}</div>
                    <div className="text-xs text-blue-600">Running</div>
                </div>
                <div className="rounded-lg border border-slate-200 bg-slate-50 p-3 text-center">
                    <div className="text-2xl font-bold text-slate-700">{formatDuration(aggregated.totalDurationMs)}</div>
                    <div className="text-xs text-slate-600">Total Time</div>
                </div>
            </div>

            <Separator />

            {/* Iteration list */}
            <div>
                <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-medium text-slate-700">All Iterations</h4>
                    {selectedIteration !== null && (
                        <button 
                            onClick={() => onSelectIteration(null)}
                            className="text-xs text-indigo-600 hover:text-indigo-800"
                        >
                            Clear selection
                        </button>
                    )}
                </div>
                <ScrollArea className="h-[calc(95vh-20rem-9rem)] rounded-md border border-slate-200">
                    <div className="divide-y divide-slate-100">
                        {sortedIterations.map(([index, checkpoint]) => (
                            <button
                                key={index}
                                onClick={() => onSelectIteration(index)}
                                className={cn(
                                    "w-full px-4 py-3 flex items-center gap-3 text-left transition-colors",
                                    "hover:bg-slate-50 focus:bg-slate-50 focus:outline-none",
                                    selectedIteration === index && "bg-indigo-50 hover:bg-indigo-100"
                                )}
                            >
                                {/* Status icon */}
                                <div className="flex-shrink-0">
                                    <IterationStatusIcon status={checkpoint.status} />
                                </div>

                                {/* Main content */}
                                <div className="flex-1 min-w-0">
                                    <div className="flex items-center gap-2">
                                        <span className="font-medium text-slate-800">
                                            Iteration #{index}
                                        </span>
                                        <Badge variant={getStatusVariant(checkpoint.status)} className="h-5">
                                            {checkpoint.status}
                                        </Badge>
                                    </div>
                                    <div className="flex items-center gap-3 mt-1 text-xs text-slate-500">
                                        {checkpoint.duration_ms !== null && (
                                            <span className="flex items-center gap-1">
                                                <Clock className="h-3 w-3" />
                                                {formatDuration(checkpoint.duration_ms)}
                                            </span>
                                        )}
                                        <span className="flex items-center gap-1">
                                            <Hash className="h-3 w-3" />
                                            Attempt {checkpoint.attempt}
                                        </span>
                                    </div>
                                </div>

                                {/* Arrow */}
                                <ChevronRight className={cn(
                                    "h-4 w-4 transition-colors flex-shrink-0",
                                    selectedIteration === index ? "text-indigo-600" : "text-slate-300"
                                )} />
                            </button>
                        ))}
                    </div>
                </ScrollArea>
            </div>
        </div>
    );
}

/**
 * Status icon for iteration row.
 */
function IterationStatusIcon({ status }: { status: string }) {
    switch (status) {
        case 'completed':
            return <CheckCircle2 className="h-5 w-5 text-green-600" />;
        case 'failed':
            return <XCircle className="h-5 w-5 text-red-600" />;
        case 'running':
            return <Loader2 className="h-5 w-5 text-blue-600 animate-spin" />;
        default:
            return <Clock className="h-5 w-5 text-slate-400" />;
    }
}

/**
 * Full text drawer for viewing truncated strings.
 * Appears as a bottom drawer within the inspector.
 */
function FullTextDrawer({ 
    isOpen, 
    onClose, 
    text, 
    keyName 
}: { 
    isOpen: boolean; 
    onClose: () => void; 
    text: string; 
    keyName?: string;
}) {
    const [copied, setCopied] = useState(false);

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(text);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Failed to copy:', err);
        }
    };

    return (
        <AnimatePresence>
            {isOpen && (
                <motion.div
                    initial={{ x: '100%' }}
                    animate={{ x: 0 }}
                    exit={{ x: '100%' }}
                    transition={{ type: 'spring', damping: 25, stiffness: 300 }}
                    className="absolute inset-y-0 right-0 z-10 flex w-full flex-col border-l border-slate-200 bg-white shadow-lg"
                >
                    {/* Header */}
                    <div className="px-6 py-2 flex items-center justify-between border-b border-slate-100 flex-shrink-0">
                        <div className="flex items-center gap-2">
                            <span className="text-sm font-medium text-slate-700">
                                {keyName ? `Full Text: ${keyName}` : 'Full Text'}
                            </span>
                            <Badge variant="secondary" className="text-xs">
                                {text.length.toLocaleString()} chars
                            </Badge>
                        </div>
                        <div className="flex items-center gap-2">
                            <button
                                onClick={handleCopy}
                                className="flex items-center gap-1 px-2 py-1 text-xs text-slate-600 hover:text-slate-800 hover:bg-slate-100 rounded transition-colors"
                            >
                                {copied ? (
                                    <>
                                        <CheckCircle2 className="h-3 w-3 text-green-600" />
                                        Copied
                                    </>
                                ) : (
                                    <>
                                        <Copy className="h-3 w-3" />
                                        Copy
                                    </>
                                )}
                            </button>
                            <button
                                onClick={onClose}
                                className="p-1 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded transition-colors"
                            >
                                <X className="h-4 w-4" />
                            </button>
                        </div>
                    </div>
                    
                    {/* Content */}
                    <ScrollArea className="flex-1 px-10 py-4">
                        <pre className="whitespace-pre-wrap break-words font-mono text-sm text-slate-800 leading-relaxed">
                            {text}
                        </pre>
                    </ScrollArea>
                </motion.div>
            )}
        </AnimatePresence>
    );
}

/**
 * Empty state placeholder.
 */
function EmptyState({ message }: { message: string }) {
    return (
        <div className="rounded-md border border-dashed border-slate-200 bg-slate-50 px-4 py-6 text-center text-sm text-slate-500">
            {message}
        </div>
    );
}
